"use client"

import { useState, useEffect } from "react"
import { useLocation, Link, useNavigate } from "react-router-dom"
import { Search, Grid, List, Tag, ArrowLeft } from "lucide-react"
import "./market-products.css"
import placeholder from "../../images/placeholder.png"

const MarketProducts = () => {
  const [products, setProducts] = useState([])
  const [filteredProducts, setFilteredProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [viewMode, setViewMode] = useState("grid")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const location = useLocation()
  const navigate = useNavigate()
  const { categoryId, categoryName } = location.state || {}

  const API_BASE_URL = "https://imperia-backend.onrender.com/api/products"
  const BEARER_TOKEN = "YWRtaW5AZXhhbXBsZS5jb206c3VwZXJzZWN1cmU="

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${BEARER_TOKEN}`,
    "Content-Type": "application/json",
  })

  // Helper to parse tags reliably, copied from your Products component
  const parseTags = (tags) => {
    if (!tags) return []
    if (typeof tags === "string") {
      return tags.split(",").map((tag) => tag.trim()).filter(Boolean)
    }
    if (Array.isArray(tags)) {
      return tags.map((tag) => (typeof tag === "string" ? tag.trim() : "")).filter(Boolean)
    }
    return []
  }
  
  // Utility to format category name, copied from your Products component
  const formatCategory = (cat) =>
    cat.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())


  const generateProductData = (product) => ({
    id: product.productId,
    name: product.name || "Premium Product",
    path: `/products/${product.productId}`,
    image: product.imageUrl || placeholder,
    // Description is kept for search functionality but not displayed on the card
    description: product.description || "High-quality product engineered for superior performance.",
    price: product.price || 0,
    category: product.category || "uncategorized",
    tags: product.tags || [], // Added tags property
  })

  useEffect(() => {
    const fetchProductsByCategory = async () => {
      if (!categoryId) return

      setLoading(true)
      setError(null)

      try {
        const response = await fetch(`${API_BASE_URL}/category/${categoryId}`, {
          method: "GET",
          headers: getAuthHeaders(),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()
        if (result.success && result.data && Array.isArray(result.data)) {
          const productsData = result.data.map(generateProductData)
          setProducts(productsData)
          setFilteredProducts(productsData)
        } else {
          throw new Error("Invalid API response format")
        }
      } catch (error) {
        console.error("Error fetching products by category:", error)
        setError(error.message)
        setProducts([])
        setFilteredProducts([])
      } finally {
        setLoading(false)
      }
    }

    fetchProductsByCategory()
  }, [categoryId])

  useEffect(() => {
    let filtered = products

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase()
      filtered = filtered.filter(
        (product) => {
          const tagsString = parseTags(product.tags).join(' ').toLowerCase()
          return (
            product.name.toLowerCase().includes(lowerCaseSearchTerm) ||
            product.description.toLowerCase().includes(lowerCaseSearchTerm) ||
            tagsString.includes(lowerCaseSearchTerm)
          )
        }
      )
    }

    setFilteredProducts(filtered)
  }, [searchTerm, products])

  const handleProductClick = (path) => {
    navigate(path)
  }

  // Updated ProductCard component, styled like your reference
  const ProductCard = ({ product }) => {
    const tagsArray = parseTags(product.tags).slice(0, 3)

    return (
      <article
        className={`product-card ${viewMode === "grid" ? "grid-card" : "list-card"}`}
        onClick={() => handleProductClick(product.path)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleProductClick(product.path)}
        aria-label={`View details for ${product.name}, category ${formatCategory(
          product.category
        )}, Tags: ${tagsArray.join(", ")}`}
      >
        <div className="product-image-container">
          <img
            src={product.image || placeholder}
            alt={product.name}
            onError={(e) => { e.target.src = placeholder }}
            className="product-image"
          />
          <div className="product-id-badge">
            <span>{product.id}</span>
          </div>
        </div>
        <div className="product-content">
          <div className="product-category-badge">
            <Tag size={12} />
            <span>{formatCategory(product.category)}</span>
          </div>
          <h3 className="product-title">{product.name}</h3>
          
          {/* SEO-Optimized Tags Display */}
          {tagsArray.length > 0 && (
            <div className="product-tags-container">
              {tagsArray.map((tag, index) => (
                <span
                  key={index}
                  className="product-tag-pill"
                  title={`${tag} - Premium quality ${formatCategory(product.category)}`}
                >
                  <span className="product-tag-dot"></span>
                  <span className="tag-text">{tag}</span>
                </span>
              ))}
            </div>
          )}
          
          <div className="product-actions">
            <Link to={product.path} className="btn-primary">
              View Details
            </Link>
          </div>
        </div>
      </article>
    )
  }

  return (
    <div className="products-page">
      <section className="products-section">
        <div className="products-container">
          <Link
            to="/market"
            className="back-link"
            style={{
              marginBottom: "12px",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              backgroundColor: "#f8f9fa",
              color: "#495057",
              textDecoration: "none",
              borderRadius: "6px",
              border: "1px solid #dee2e6",
              fontSize: "14px",
              fontWeight: "500",
              transition: "all 0.2s ease",
            }}
          >
            <ArrowLeft size={20} />
            <span>Back to Markets</span>
          </Link>
          <div className="products-header">
            <div className="products-count">
              Showing {filteredProducts.length} products {categoryName && `for ${categoryName}`}
            </div>
            <div className="filters-controls">
              <div className="view-toggle">
                <button
                  className={`view-button ${viewMode === "grid" ? "active" : ""}`}
                  onClick={() => setViewMode("grid")}
                  aria-label="Grid view"
                >
                  <Grid size={16} />
                </button>
                <button
                  className={`view-button ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => setViewMode("list")}
                  aria-label="List view"
                >
                  <List size={16} />
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="loading-text">Loading products...</p>
            </div>
          ) : error ? (
            <div className="empty-state">
              <h3>Failed to load products</h3>
              <p>Error: {error}</p>
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className={`products-grid ${viewMode}-view`}>
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <h3>No products found</h3>
              <p>No products available in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default MarketProducts